/**
 * Winkelmodus sort/group core — the pure half of the supermarket-category
 * feature. Deliberately dependency-free: no i18n, no window, no api. The
 * category ORDER arrives as an argument rather than an import, which keeps
 * the Dutch display vocabulary (shoplist-categories.js) out of here and
 * makes the whole module unit-testable with a plain static import
 * (shoplist-sort.test.mjs, written red before this file existed).
 *
 * These are pure functions of the array they are handed, with no memo
 * keyed on identity: winkelmodus replaces the whole item list every ~10s
 * poll tick, so a cache would miss on every tick and only add a way to be
 * wrong. Sorting a shopping list is not the expensive part of that render.
 *
 * Nothing here decides anything the server already decided — it reorders
 * rows for display and nothing else. The stored category enum itself lives
 * in ETM_Lists::VALID_ITEM_CATEGORIES; this file never validates a slug,
 * it only recognises one or files it under "everything else".
 */

export const SORT_DEFAULT = 'default';
export const SORT_ALPHA = 'alpha';
export const SORT_CATEGORY = 'category';

/**
 * Resolves a possibly-untrustworthy stored sort mode against the list's
 * current grouping flag.
 *
 * Two distinct problems, one call. The stored string comes from
 * localStorage, which is user-writable and survives a list being changed
 * out from under it — so an unknown value must degrade rather than render
 * nothing. And 'category' on a list whose grouping was switched off has no
 * groups to draw; the same falsy input covers the moment before the list
 * meta has loaded, so the screen never flashes an empty grouped view.
 *
 * The preference is only *ignored*, never rewritten: turning grouping back
 * on restores the category view the household last chose.
 *
 * @param {unknown} stored
 * @param {boolean} groupByCategory
 * @returns {'default'|'alpha'|'category'}
 */
export function sanitizeSortMode(stored, groupByCategory) {
  if (stored === SORT_ALPHA) return SORT_ALPHA;
  if (stored === SORT_CATEGORY && groupByCategory) return SORT_CATEGORY;
  return SORT_DEFAULT;
}

/**
 * Alphabetical by label, Dutch collation, case- and accent-insensitive
 * (sensitivity 'base' — a shopper scanning for "eclairs" should not have
 * to know the label was typed "Éclairs").
 *
 * Equal labels fall through to a total tiebreak on the stringified id.
 * That is not cosmetic: without it the comparator returns 0 for two
 * different rows with the same label, and while Array.prototype.sort is
 * specified stable, the real hazard the WP-2 review caught was a
 * comparator that could return the same sign for compare(a,b) and
 * compare(b,a) — which the spec answers with engine-defined, i.e. no,
 * ordering. String(id) is used rather than a numeric compare because ids
 * are mixed-type here: optimistic rows carry a 'tmp-N' string id until
 * their add is acked.
 *
 * Returns a new array; the input is never mutated.
 *
 * @param {Array<object>} items
 * @returns {Array<object>}
 */
export function sortItemsAlpha(items) {
  return [...items].sort((a, b) => {
    const cmp = String(a.label || '').localeCompare(String(b.label || ''), 'nl', { sensitivity: 'base' });
    if (cmp !== 0) return cmp;
    return String(a.id).localeCompare(String(b.id));
  });
}

/**
 * Partitions items into the supermarket's walk order.
 *
 * A stable filter partition, deliberately NOT a sort: the order the caller
 * hands in (the list's own position order, which is the household's
 * manual arrangement) survives untouched inside each group. Re-sorting
 * here would silently discard that the moment this file's idea of "right"
 * drifted from the list's — the same reasoning
 * groupSubscriptionsByCategory() documents for itself.
 *
 * Groups appear in `categoryOrder`'s order and only when non-empty, so a
 * list with three tagged items never renders nine headers. Everything the
 * order does not recognise — null, an item with no `category` key at all
 * (every optimistic temp row, until its add is acked and the server's own
 * value arrives), '' , or a slug retired from the set — collects in
 * exactly one bucket with `category: null`, always last. The caller
 * renders that as "Overig"; it is a display bucket and is never stored.
 *
 * @param {Array<object>} items
 * @param {Array<string>} categoryOrder Stored slugs, in walk order.
 * @returns {Array<{category: string|null, items: Array<object>}>}
 */
export function groupItemsByCategory(items, categoryOrder) {
  const known = new Set(categoryOrder);

  const groups = categoryOrder
    .map((category) => ({
      category,
      items: items.filter((i) => i.category === category),
    }))
    .filter((group) => group.items.length > 0);

  const rest = items.filter((i) => !known.has(i.category));
  if (rest.length > 0) {
    groups.push({ category: null, items: rest });
  }

  return groups;
}
